import { DateTime } from 'luxon'
import { ref } from 'valtio'
// import toast from 'react-hot-toast'
import { authState } from '../store/authState.ts'
import { dataState } from '../store/dataState.ts'
import { historyState } from '../store/historyState.ts'
import { mapState } from '../store/mapState.ts'
import { post } from './apiHelper.ts'
import { HISTORY_BY_TEN_URL } from './urls.ts'

export const getHistoryLabel = (subType: string, status?: number) => {
    // status 2 = no motion — override the message type label
    if (status === 2) return 'No motion'

    let label
    switch (subType) {
        case 'powerUp':
            label = 'Power on'
            break
        case 'powerDown':
            label = 'Power off'
            break
        case 'fullCharge':
            label = 'Full charge'
            break
        case 'batteryAlert':
            label = 'Low battery'
            break
        case 'find':
            label = 'Find'
            break
        case 'sleepResponse':
            label = 'Sleep set'
            break
        case 'DeviceAwoke':
        case 'wakeResponse':
            label = 'Wake'
            break
        case 'beaconInRange':
            label = 'Power save on'
            break
        case 'beaconOutOfRange':
            label = 'Power save off'
            break
        case 'inCharger':
            label = 'In charger'
            break
        case 'outOfCharger':
            label = 'Out of charger'
            break
        case 'DeviceIsSleeping':
            label = 'Sleeping'
            break
        default:
            label = 'Check-in report'
    }
    return label
}

export const getNextPet = (petList: string[], curPet: string) => {
    let idx = petList.findIndex(p => p === curPet)
    ++idx
    if (idx === petList.length) {
        idx = 0
    }
    // console.log('idx', idx, petList.length)
    return petList[idx]
}

export const getPrevPet = (petList: string[], curPet: string) => {
    let idx = petList.findIndex(p => p === curPet)
    --idx
    if (idx < 0) {
        idx = petList.length - 1
    }
    return petList[idx]
}

const getNextStartDate = () => {
    const locations = historyState.locations
    const last = locations.length - 1
    return locations[last].properties.receivedTime
}

const getPreviousStartDate = () => {
    const locations = historyState.locations
    return locations?.[0].properties.receivedTime
}

const getDateBasedOnDirection = (direction: 'forward' | 'back' | 'init' | 'reset', start: string) => {
    if (direction === 'init' || direction === 'reset') {
        return start
    }
    if (direction === 'back') {
        return getNextStartDate()
    }
    return getPreviousStartDate()
}

export const bundleApiData = (direction: 'forward' | 'back' | 'init' | 'reset', start: string, petId: string) => {
    const date = getDateBasedOnDirection(direction, start)
    const device = dataState.devices.find(d => d.petId === petId)

    return {
        pet: petId,
        iccid: device?.iccid,
        date
    }
}

export const getHistoryByTen = async (
    start: string,  // ISO date time string
    direction: 'forward' | 'back' | 'init' | 'reset',
    petId?: string
    , cancelToken?: any) => {
    const zone = authState.timezone
    const selectedPet = historyState.selectedPetId
    const includeInvalid = historyState.includeInvalid

    const apiBundle = bundleApiData(direction, start, selectedPet || petId!)
    try {
        const res = await post(HISTORY_BY_TEN_URL, {
            petIccid: [apiBundle],
            direction,
            includeInvalid,
        }, cancelToken)
        if (res?.status === 200) {
            const flat = res.data.flat || []
            if (flat.length) {
                // calculate the new start & end dates, set the earliest date if first time
                let start = flat[0].properties.receivedTime
                if (res.data.points)
                    if (!flat.length && direction === 'forward') {
                        const earliestDate = historyState.earliestDate
                        start = earliestDate.toSeconds()
                    }
                const end = flat[flat.length - 1]?.properties.receivedTime
                const firstDate = DateTime.fromISO(start).setZone(zone.value)
                let update = {}

                if (direction === 'init' || direction === 'reset') {
                    update = {
                        ...update,
                        earliestDate: firstDate,
                        reachedStart: true,
                    }
                } else {
                    const earliestDate = historyState.earliestDate
                    // console.log('compare earliest', earliestDate.toISO(), 'to', firstDate.toISO())
                    if (firstDate.toISO()! >= earliestDate.toISO()!) {
                        update = {
                            ...update,
                            reachedStart: true,
                        }
                    } else {
                        update = {
                            ...update,
                            reachedStart: false,
                        }
                    }
                }
                update = {
                    ...update,
                    noMoreOlder: false,
                    locations: flat,
                    startDate: DateTime.fromISO(start).setZone(zone.value),
                    endDate: DateTime.fromISO(end).setZone(zone.value)
                }
                return update
            } else {
                // Empty result — no more data in this direction.
                // Keep current locations visible, disable the older button.
                return {
                    noMoreOlder: true,
                }
            }
        } else {
            // toast.error('Error getting history')
            mapState.map?.fitBounds(mapState.bounds!, { left: 100 })
        }
    } catch (e) {
        console.log('getHistory error', e)
    }
}

// back in time
export const backTen = async (e: any) => {
    e.stopPropagation()
    return await getHistoryByTen(getNextStartDate(), 'back', undefined)
}

// forward in time
export const forwardTen = async (e: any) => {
    e.stopPropagation()
    return await getHistoryByTen(getPreviousStartDate(), 'forward', undefined)
}

/**
 * Fetch older history and append to existing locations (for continuous mobile carousel).
 * Unlike backTen which replaces locations, this merges them.
 * Returns true if new data was loaded, false if no more data.
 */
export const loadOlderAndAppend = async (): Promise<boolean> => {
    const locations = historyState.locations
    if (!locations.length) return false

    const oldestTime = locations[locations.length - 1].properties.receivedTime
    const zone = authState.timezone
    const selectedPet = historyState.selectedPetId
    const includeInvalid = historyState.includeInvalid

    if (!selectedPet) return false

    const apiBundle = bundleApiData('back', oldestTime, selectedPet)
    try {
        const res = await post(HISTORY_BY_TEN_URL, {
            petIccid: [apiBundle],
            direction: 'back',
            includeInvalid,
        })
        if (res?.status === 200) {
            const flat = res.data.flat || []
            if (flat.length) {
                // Append new points to existing locations
                historyState.locations = [...locations, ...flat]
                // Update endDate to reflect the oldest point
                const end = flat[flat.length - 1]?.properties.receivedTime
                historyState.endDate = ref(DateTime.fromISO(end).setZone(zone.value)) as unknown as DateTime
                historyState.noMoreOlder = false
                return true
            } else {
                historyState.noMoreOlder = true
                return false
            }
        }
    } catch (e) {
        console.log('loadOlderAndAppend error', e)
    }
    return false
}
